import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ArticleIcon from "@mui/icons-material/Article";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";
import { Document } from "../../Models/company";
import { isPreviewableFile } from "../../Helpers/documentHelper";
import { ProcessingStatus, UserRole } from "../../Enums/companyStatus";
import { hasRole } from "../../Helpers/authHelpers";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";

interface UploadedDocumentsListProps {
  documents: Document[];
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onStopDocument: (documentId: number) => void;
  onViewLogs: (documentId: number) => void;
  onDownloadDocument: (documentId: number, fileName: string) => void;
  onDownloadChunks: (documentId: number) => void;
  onDeleteDocument: (documentId: number) => void;
  onPreviewDocument?: (documentId: number, fileName: string) => void;
  onDetachDocument?: (documentId: number) => void;
  isDownloading?: boolean;
}

const UploadedDocumentsList: React.FC<UploadedDocumentsListProps> = ({
  documents,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onStopDocument,
  onViewLogs,
  onDownloadDocument,
  onDownloadChunks,
  onDeleteDocument,
  onPreviewDocument,
  onDetachDocument,
  isDownloading = false,
}) => {
  const { t } = useTranslation("documentUpload");

  if (documents.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          padding: "48px 24px",
          backgroundColor: color.gray50,
          borderRadius: "8px",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            margin: 0,
            fontSize: "0.875rem",
            color: color.gray500,
          }}
        >
          {t("documentUpload.noUploadedFiles")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginBottom: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {documents.map((file) => {
            const isStopped = file.status === ProcessingStatus.STOPPED;

            return (
              <Box
                key={file.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: isStopped ? color.gray200 : color.white,
                  borderRadius: "6px",
                  border: `1px solid ${color.gray200}`,
                  padding: "12px",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: `${color.primary_dark}0F`,
                    borderColor: color.accent,
                  },
                }}
              >
                {file.status !== ProcessingStatus.IN_PROGRESS ? (
                  <DescriptionIcon sx={{ color: color.primary_dark, mr: 1 }} />
                ) : (
                  <CircularProgress
                    size={16}
                    sx={{ color: color.accent, mr: 1 }}
                  />
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip
                    title={
                      file.question ? (
                        <Box>
                          <Typography
                            sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                          >
                            {file.name}
                          </Typography>

                          <Typography sx={{ fontSize: "0.7rem", opacity: 0.8 }}>
                            {t("documentUpload.category")}{" "}
                            {file.question.category}
                          </Typography>

                          <Typography sx={{ fontSize: "0.7rem", opacity: 0.8 }}>
                            {t("documentUpload.question")}{" "}
                            {file.question.question}
                          </Typography>
                        </Box>
                      ) : (
                        file.name
                      )
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          whiteSpace: "nowrap",
                          maxWidth: "none",
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: color.gray900,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textDecoration: isStopped ? "line-through" : "none",
                      }}
                    >
                      {file.name}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: "4px", ml: 2 }}>
                  {onPreviewDocument &&
                    isPreviewableFile(file.name) &&
                    !isStopped && (
                      <Tooltip title={t("documentUpload.preview")}>
                        <IconButton
                          size="small"
                          sx={{
                            padding: "8px",
                            "&:hover": { backgroundColor: color.blue50 },
                          }}
                          onClick={() => onPreviewDocument(file.id, file.name)}
                          disabled={
                            file.status === ProcessingStatus.IN_PROGRESS
                          }
                        >
                          <VisibilityIcon
                            sx={{
                              fontSize: "16px",
                              color:
                                file.status === ProcessingStatus.IN_PROGRESS
                                  ? color.gray400
                                  : color.accent,
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  {file.status === ProcessingStatus.IN_PROGRESS && (
                    <Tooltip title={t("documentUpload.stopProcessing")}>
                      <IconButton
                        size="small"
                        sx={{
                          padding: "8px",
                          "&:hover": { backgroundColor: color.red50 },
                        }}
                        onClick={() => onStopDocument(file.id)}
                      >
                        <CloseIcon
                          sx={{
                            fontSize: "16px",
                            color: color.red600,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  {file.question && onDetachDocument && (
                    <Tooltip title={t("documentUpload.detachQuestion")}>
                      <IconButton
                        size="small"
                        sx={{
                          padding: "8px",
                          "&:hover": { backgroundColor: color.blue50 },
                        }}
                        onClick={() => onDetachDocument(file.id)}
                      >
                        <LinkOffIcon
                          sx={{ fontSize: "16px", color: color.accent }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasRole(UserRole.ADMIN) && (
                    <Tooltip title={t("documentUpload.viewLogs")}>
                      <IconButton
                        size="small"
                        sx={{
                          padding: "8px",
                          "&:hover": { backgroundColor: color.blue50 },
                        }}
                        onClick={() => onViewLogs(file.id)}
                      >
                        <ArticleIcon
                          sx={{ fontSize: "16px", color: color.accent }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("documentUpload.downloadDocument")}>
                    <IconButton
                      size="small"
                      sx={{
                        padding: "8px",
                        "&:hover": { backgroundColor: color.blue50 },
                      }}
                      onClick={() => onDownloadDocument(file.id, file.name)}
                      disabled={
                        isDownloading ||
                        file.status === ProcessingStatus.IN_PROGRESS ||
                        file.status === ProcessingStatus.STOPPED
                      }
                    >
                      <CloudDownloadIcon
                        sx={{
                          fontSize: "16px",
                          color:
                            isDownloading ||
                            file.status === ProcessingStatus.IN_PROGRESS ||
                            file.status === ProcessingStatus.STOPPED
                              ? color.gray400
                              : color.accent,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                  {hasRole(UserRole.ADMIN) && (
                    <Tooltip title={t("documentUpload.downloadChunks")}>
                      <IconButton
                        size="small"
                        sx={{
                          padding: "8px",
                          "&:hover": { backgroundColor: color.blue50 },
                        }}
                        onClick={() => onDownloadChunks(file.id)}
                        disabled={
                          file.status === ProcessingStatus.IN_PROGRESS ||
                          file.status === ProcessingStatus.STOPPED
                        }
                      >
                        <CloudDownloadOutlinedIcon
                          sx={{
                            fontSize: "16px",
                            color:
                              file.status === ProcessingStatus.IN_PROGRESS ||
                              file.status === ProcessingStatus.STOPPED
                                ? color.gray400
                                : color.accent,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("documentUpload.deleteDocument")}>
                    <IconButton
                      size="small"
                      sx={{
                        padding: "8px",
                        "&:hover": { backgroundColor: color.red50 },
                      }}
                      onClick={() => onDeleteDocument(file.id)}
                      disabled={file.status === ProcessingStatus.IN_PROGRESS}
                    >
                      <DeleteIcon
                        sx={{
                          fontSize: "16px",
                          color:
                            file.status === ProcessingStatus.IN_PROGRESS
                              ? color.gray400
                              : color.red600,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <TablePagination
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        rowsPerPageOptions={[]}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        sx={{
          marginTop: 4,
          color: color.primary_dark,
          "& .MuiTablePagination-displayedRows": {
            color: color.primary_dark,
          },
          "& .MuiTablePagination-toolbar": {
            color: color.primary_dark,
          },
        }}
      />
    </>
  );
};

export default UploadedDocumentsList;
